import { Slider } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { floatNumberOfSteps } from 'common/constants';
import { FilterValues } from 'features/resultFiltering/resultFilteringSlice';
import React from 'react';

const useSliderStyles = makeStyles((theme: Theme) =>
  createStyles({
    valueLabel: {
      backgroundColor: theme.palette.grey[700],
      borderRadius: 4,
      top: 25,
      left: 'unset',
      padding: 4,
      '& *': {
        background: 'transparent',
        color: theme.palette.grey[100],
        height: 'auto',
        width: 'auto',
      },
    },
    mark: {
      height: 8,
      width: 1,
      marginTop: -3,
    },
    markActive: {
      opacity: 1,
      backgroundColor: 'currentColor',
    },
  }),
);

const useThumbStyles = makeStyles((theme: Theme) =>
  createStyles({
    thumb: {
      backgroundColor: theme.palette.grey[100],
      border: '2px solid currentColor',
      '&:focus, &:hover, &$active': {
        backgroundColor: theme.palette.grey[100],
        border: '2px solid currentColor',
      },
    },
  }),
);

/*
 * Factory function that creates thumb components for the slider that display
 * the lowerChange and upperChanged filter fields.
 */
const createThumb = (lowerChanged: boolean, upperChanged: boolean) => (props: any) => {
  const classes = useThumbStyles();
  const index = props['data-index'] as number;

  let classNames;
  if ((index === 0 && lowerChanged) || (index === 1 && upperChanged)) {
    classNames = props.className;
  } else {
    classNames = `${props.className} ${classes.thumb}`;
  }

  return <span {...props} className={classNames}></span>;
};

interface Props {
  property: { name: string; description: string };
  isInteger: boolean;
  values: [number, number];
  min: number;
  max: number;
  marks: [number, number];
  lowerChanged: boolean;
  upperChanged: boolean;
  handleChange: (_: FilterValues) => void;
  handleChangeCommit: (_: FilterValues) => void;
}

const FilterSlider = ({
  property,
  isInteger,
  values,
  min,
  max,
  marks: [lowerMark, upperMark],
  lowerChanged,
  upperChanged,
  handleChange,
  handleChangeCommit,
}: Props) => {
  const classes = useSliderStyles();

  const step = isInteger ? 1 : Number(((max - min) / floatNumberOfSteps).toPrecision(1));

  return (
    <Slider
      classes={classes}
      value={values}
      min={isInteger ? min - 0.5 : min}
      max={isInteger ? max + 0.5 : max}
      step={step}
      // Don't need SyntheticEvents
      onChange={(_, v) => handleChange(v as FilterValues)}
      onChangeCommitted={(_, v) => handleChangeCommit(v as FilterValues)}
      valueLabelDisplay="on"
      valueLabelFormat={(x) => x.toFixed(isInteger ? 0 : 2)}
      aria-labelledby={`${property.name}-slider`}
      getAriaValueText={(v) => `${property.name}=${v}`}
      marks={[
        { value: lowerMark, label: '' },
        { value: upperMark, label: '' },
      ]} // Display the current group data range
      ThumbComponent={createThumb(lowerChanged, upperChanged)}
    />
  );
};

export default FilterSlider;
